import { useT } from '../../i18n/LanguageContext';
import LanguageToggle from './LanguageToggle';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  FiHome, FiCalendar, FiScissors, FiShoppingCart, FiUsers, FiUserCheck,
  FiPackage, FiGift, FiSend, FiBarChart2, FiSettings, FiShield, FiPercent,
  FiX, FiFileText, FiStar, FiBookOpen, FiDroplet, FiDivideCircle, FiLogOut, FiActivity,
  FiShare2, FiGrid, FiPieChart, FiList, FiClipboard, FiChevronRight, FiHeart, FiClock, FiCamera,
  FiCheckSquare,
  FiChevronsLeft, FiChevronsRight, FiBell, FiCreditCard, FiMessageCircle, FiTarget, FiAlertTriangle,
  FiTrendingUp, FiCompass, FiInstagram, FiAward, FiLayers,
} from 'react-icons/fi';
import { FaRupeeSign } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { hasAccess } from '../../utils/permissions';

const NAV = [
  {
    label: 'Operations',
    items: [
      /* The landing page, and until now reachable only by typing the URL —
         which is why nobody had seen it. */
      { id: 'dashboard',      label: 'Dashboard',         icon: FiHome,         path: '/' },
      { id: 'walk-ins',       label: 'Check-ins',         icon: FiUserCheck,    path: '/walk-ins' },
      { id: 'billing',        label: 'Billing',           icon: FiShoppingCart, path: '/billing' },
      { id: 'appointments',   label: 'Appointments',      icon: FiCalendar,     path: '/appointments' },
      { id: 'services',       label: 'Services',          icon: FiScissors,     path: '/services' },
      // Beside Services, because it is the same menu read from the customer's
      // side: what they came in with rather than what we do.
      { id: 'services',       label: 'Concerns',          icon: FiHeart,        path: '/concerns' },
      /* Service SOP lived here and is now inside each service's own page,
         where the steps sit beside the price and the products they use. The
         route stays mounted for old links. */
      { id: 'tasks',                 label: 'Tasks',             icon: FiCheckSquare, path: '/tasks' },
      { id: 'attendance-tracker',    label: 'Attendance',        icon: FiClock,     path: '/attendance-tracker' },
      { id: 'transactions',          label: 'Transactions',      icon: FiFileText,  path: '/transactions' },
      { id: 'whatsapp',       label: 'WhatsApp',          icon: FiMessageCircle, path: '/whatsapp' },
      { id: 'my-performance', label: 'Staff Performance', icon: FiActivity,     path: '/my-performance' },
      { id: 'recognition',    label: 'Recognition',       icon: FiAward,        path: '/recognition' },
    ],
  },
  {
    label: 'Special Services',
    items: [
      { id: 'bridal',        label: 'Events',         icon: FiHeart,     path: '/events' },
      { id: 'beauty-profile', label: 'Beauty Profile', icon: FiClipboard, path: '/beauty-profile' },
      { id: 'customers',      label: 'AI Consultations', icon: FiCamera,  path: '/ai-consultations' },
    ],
  },
  {
    label: 'Management',
    items: [
      { id: 'customers',        label: 'Customers',         icon: FiUsers,      path: '/customers' },
      { id: 'customer-risk',    label: 'Customer Risk',     icon: FiAlertTriangle, path: '/customer-risk' },
      { id: 'employees',        label: 'Staff',             icon: FiUserCheck,  path: '/employees' },
      { id: 'staff-roster',     label: 'Staff Roster',      icon: FiGrid,       path: '/staff-roster' },
      { id: 'staff-notes',      label: 'Staff Record',      icon: FiClipboard,  path: '/staff-notes' },
      { id: 'staff-time',       label: 'Staff Time',        icon: FiClock,      path: '/staff-time' },
      { id: 'payroll',          label: 'Payroll',           icon: FaRupeeSign,  path: '/payroll' },
      { id: 'inventory',        label: 'Products',          icon: FiPackage,    path: '/inventory' },
      { id: 'product-usage',    label: 'Product Usage',     icon: FiDroplet,    path: '/product-usage' },
      { id: 'service-profitability', label: 'Profitability',  icon: FiTrendingUp, path: '/service-profitability' },
      { id: 'pricing',          label: 'Pricing Strategy',  icon: FaRupeeSign,  path: '/pricing' },
      { id: 'salon-calendar',     label: 'Salon Calendar',    icon: FiCalendar,   path: '/salon-calendar' },
      { id: 'targets',            label: 'Targets',           icon: FiTarget,     path: '/targets' },
      { id: 'accounts',           label: 'Accounts',          icon: FiBookOpen,   path: '/accounts' },
      { id: 'ledger',             label: 'Ledger',            icon: FiFileText,   path: '/ledger' },
      { id: 'stylist-directory',  label: 'Stylist Directory', icon: FiUsers,      path: '/stylist-directory' },
    ],
  },
  {
    label: 'Growth',
    items: [
      { id: 'referrals',  label: 'Referrals',  icon: FiShare2,   path: '/referrals' },
      { id: 'packages',   label: 'Memberships', icon: FiGift,     path: '/memberships' },
      { id: 'packages',   label: 'Packages',    icon: FiPackage,  path: '/packages' },
      { id: 'packages',   label: 'Combos',      icon: FiLayers,   path: '/combos' },
      { id: 'offers',     label: 'Offers',     icon: FiPercent,  path: '/offers' },
      { id: 'marketing',  label: 'Marketing',  icon: FiSend,     path: '/marketing' },
      { id: 'social',           label: 'Social',           icon: FiInstagram, path: '/social' },
      { id: 'vip-cards',  label: 'Membership Cards', icon: FiCreditCard, path: '/vip-cards' },
      { id: 'service-reminders', label: 'Service Reminders', icon: FiBell, path: '/service-reminders' },
    ],
  },
  {
    label: 'Analytics',
    items: [
      // First in the group: it is the widest view, and the rest drill into it.
      { id: 'reports',            label: 'Salon Overview',     icon: FiCompass,    path: '/overview' },
      { id: 'reviews',            label: 'Reviews',            icon: FiStar,       path: '/reviews' },
      { id: 'reports',            label: 'Reports',            icon: FiBarChart2,  path: '/reports' },
      { id: 'customer-insights',  label: 'Customer Insights',  icon: FiPieChart,   path: '/customer-insights' },
      { id: 'service-insights',   label: 'Service Insights',   icon: FiScissors,   path: '/service-insights' },
      { id: 'service-trends',     label: 'Service Trends',     icon: FiTrendingUp, path: '/service-trends' },
      { id: 'staff-insights',      label: 'Staff Insights',     icon: FiUserCheck,  path: '/staff-insights' },
      { id: 'product-insights',   label: 'Product Insights',   icon: FiPackage,    path: '/product-insights' },
    ],
  },
  {
    label: 'System',
    items: [
      { id: 'users',    label: 'User Management', icon: FiShield,   path: '/users' },
      { id: 'documents', label: 'Documents',     icon: FiFileText, path: '/documents' },
      { id: 'settings', label: 'Settings',        icon: FiSettings, path: '/settings' },
      { id: 'system-config', label: 'How it runs',   icon: FiClock,    path: '/system-config' },
    ],
  },
];

export default function OSidebar({ open, onClose, collapsed = false, onToggleCollapse }) {
  const t = useT();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const go = (path) => {
    navigate(path);
    if (window.innerWidth < 1024) onClose();
  };

  return (
    <>
      {/* Mobile overlay */}
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/60 backdrop-blur-sm lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        style={{ background: 'var(--os-navy-950)' }}
        className={`
          os-sidebar ${collapsed ? 'os-sidebar--rail' : ''}
          fixed lg:relative inset-y-0 left-0 z-40 flex flex-col h-shell
          transition-transform duration-300
          ${open ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        {/* Logo */}
        <div className="os-rail-center flex items-center justify-between px-5 pt-6 pb-5 shrink-0">
          <span
            className="os-rail-hide font-display text-[1.25rem] font-semibold tracking-wide"
            style={{ color: '#DBAA3D' }}
          >
            Gumma's Glam House
          </span>
          {/* The monogram stands in for the wordmark on the rail */}
          {collapsed && (
            <span
              className="hidden lg:flex font-display text-[1.4167rem] font-semibold"
              style={{ color: '#DBAA3D' }}
              title="Gumma's Glam House"
            >
              G
            </span>
          )}
          <button
            onClick={onClose}
            className="lg:hidden p-1.5 rounded-lg transition-colors"
            style={{ color: 'rgba(255,255,255,0.4)' }}
          >
            <FiX className="w-4 h-4" />
          </button>
        </div>

        {/* Collapse toggle — desktop only, since the rail is a desktop idea */}
        {onToggleCollapse && (
          <button
            onClick={onToggleCollapse}
            title={collapsed ? 'Expand menu' : 'Collapse menu'}
            aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
            aria-expanded={!collapsed}
            className="os-rail-center hidden lg:flex items-center gap-2 mx-3 mb-2 px-2.5 py-1.5 rounded-lg transition-colors shrink-0"
            style={{ color: 'rgba(255,255,255,0.4)' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            {collapsed ? <FiChevronsRight className="w-4 h-4" /> : <FiChevronsLeft className="w-4 h-4" />}
            <span className="os-rail-hide text-[0.9167rem] font-medium">Collapse</span>
          </button>
        )}

        {/* Divider */}
        <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', marginInline: 20, marginBottom: '0.6667rem' }} />

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-3 py-2 space-y-5 scrollbar-hide">
          {NAV.map((section) => {
            const visible = section.items.filter(
              (item) => !item.id || hasAccess(user?.role, item.id)
            );
            if (!visible.length) return null;
            return (
              <div key={section.label}>
                <p
                  className="os-rail-hide px-2 mb-1.5 text-[0.75rem] font-semibold uppercase tracking-[0.2em]"
                  style={{ color: 'rgba(255,255,255,0.28)' }}
                >
                  {t(section.label)}
                </p>
                {/* On the rail the group heading has no room, so a hairline
                    keeps the grouping legible instead of losing it entirely. */}
                {collapsed && (
                  <div
                    className="hidden lg:block mx-3 mb-2"
                    style={{ height: 1, background: 'rgba(255,255,255,0.08)' }}
                  />
                )}
                <div className="space-y-0.5">
                  {visible.map((item) => {
                    const active = isActive(item.path);
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.path}
                        onClick={() => go(item.path)}
                        title={collapsed ? t(item.label) : undefined}
                        aria-label={t(item.label)}
                        className="os-rail-center w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left transition-all duration-150 group"
                        style={{
                          background: active ? 'rgba(200,149,44,0.14)' : 'transparent',
                          color: active ? '#DBAA3D' : 'rgba(255,255,255,0.58)',
                        }}
                        onMouseEnter={(e) => {
                          if (!active) {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                            e.currentTarget.style.color = 'rgba(255,255,255,0.85)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!active) {
                            e.currentTarget.style.background = 'transparent';
                            e.currentTarget.style.color = 'rgba(255,255,255,0.58)';
                          }
                        }}
                      >
                        <Icon className="w-[1.25rem] h-[1.25rem] shrink-0" />
                        <span className="os-rail-hide text-[1.0417rem] font-medium">{t(item.label)}</span>
                        {active && (
                          <div
                            className="os-rail-hide ml-auto w-1 h-1 rounded-full"
                            style={{ background: '#DBAA3D' }}
                          />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>

        {/* User + Logout */}
        <div
          className={collapsed ? 'os-rail-footer' : ''}
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)', padding: '1rem 1rem 1.3333rem' }}
        >
          <div className="os-rail-center os-rail-stack flex items-center gap-2.5 px-2 py-2 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)' }}>
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center text-[0.9167rem] font-semibold shrink-0"
              style={{ background: 'rgba(200,149,44,0.25)', color: '#DBAA3D' }}
              title={collapsed ? `${user?.name || user?.firstName || 'User'} · ${user?.role || 'Staff'}` : undefined}
            >
              {(user?.name || user?.firstName || 'U')[0].toUpperCase()}
            </div>
            <div className="os-rail-hide flex-1 min-w-0">
              <p className="text-[0.9167rem] font-medium truncate" style={{ color: 'rgba(255,255,255,0.75)' }}>
                {user?.name || user?.firstName || 'User'}
              </p>
              <p className="text-[0.8333rem] capitalize" style={{ color: 'rgba(255,255,255,0.35)' }}>
                {user?.role || 'Staff'}
              </p>
            </div>
            {/* Language sits with the person, not with the page: it is a
                choice about who is at this machine, which is exactly what the
                rest of this row is about. */}
            <span className="os-rail-hide shrink-0">
              <LanguageToggle dark compact />
            </span>
            <button
              onClick={logout}
              title="Logout"
              className="p-1.5 rounded transition-colors"
              style={{ color: 'rgba(255,255,255,0.3)' }}
              onMouseEnter={(e) => (e.currentTarget.style.color = '#EF4444')}
              onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.3)')}
            >
              <FiLogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </aside>
    </>
  );
}
